import React, { useCallback } from 'react';
import styles from './PaginatedTable.module.css';
import Select from '../Select/Select';
import RefreshIcon from '../../Icons/RefreshIcon';
import ChevronLeft from '../../Icons/ChevronLeft';
import ChevronRight from '../../Icons/ChevronRight';

const ROWS_PER_PAGE_OPTIONS = [20, 30, 50, 100];

/**
 * @param {Object} props
 * @param {number} props.page - Current page number (1-based).
 * @param {number} props.rowsPerPage - Number of rows per page.
 * @param {number} props.totalCount - Total count of items.
 * @param {object[]} props.data - Data to display.
 * @param {{ id: string, title: string }[]} props.columns - Column definitions.
 * @param {boolean} [props.newerRecordsExist] - Whether newer records exist beyond the current page.
 * @param {boolean} [props.loading] - Whether data is being loaded.
 * @param {string} [props.TooltipMessage] - Native tooltip message shown on rows.
 * @param {(row: object, cellId: string) => import('react').ReactNode} props.renderCell - Renders a cell.
 * @param {(title: string) => import('react').ReactNode} [props.renderHeaderCell] - Renders a header cell.
 * @param {(page: number) => void} props.onPageChange - Called with the new 1-based page number.
 * @param {(event: import('react').ChangeEvent<HTMLSelectElement>) => void} [props.onRowsPerPageChange] - Called when rows-per-page changes.
 * @param {(item: object) => void} [props.handleDoubleClick] - Called on row double-click.
 * @param {() => void} [props.refreshData] - Called when the reload button is clicked.
 */
const PaginatedTable = ({
  page = 1,
  rowsPerPage = 20,
  totalCount = 0,
  data = [],
  columns = [],
  newerRecordsExist,
  loading,
  TooltipMessage,
  renderCell,
  renderHeaderCell,
  onPageChange,
  onRowsPerPageChange,
  handleDoubleClick,
  refreshData,
}) => {
  const totalPages = Math.max(1, Math.ceil(totalCount / rowsPerPage));
  const rangeStart = totalCount === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, totalCount);

  const handleRefresh = useCallback(() => refreshData?.(), [refreshData]);

  const getCellStyle = useCallback((columnId) => {
    if (columnId === 'source') return { width: 28 };
    return {};
  }, []);

  return (
    <div className={styles.tablePaper}>
      <div className={styles.tableHeader}>
        <div className={styles.leftControls}>
          <button
            className={styles.reloadButton}
            onClick={handleRefresh}
            title="Reload"
            type="button"
          >
            <RefreshIcon />
          </button>
          {newerRecordsExist && (
            <span className={styles.newerRecords}>Newer Records Exist</span>
          )}
        </div>

        <div className={styles.pagination}>
          <span className={styles.rowsPerPageLabel}>Rows per page:</span>
          <Select
            options={ROWS_PER_PAGE_OPTIONS.map((opt) => ({ value: opt, label: String(opt) }))}
            value={rowsPerPage}
            onChange={(val) => onRowsPerPageChange?.({ target: { value: val } })}
            classes={{ root: styles.rowsPerPageRoot, trigger: styles.rowsPerPageTrigger, menu: styles.rowsPerPageMenu, option: styles.rowsPerPageOption }}
          />
          <span className={styles.pageRange}>
            {rangeStart}–{rangeEnd} of {totalCount}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(1)}
            disabled={page <= 1}
            title="First page"
            type="button"
          >
            &#171;
          </button>
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            title="Previous page"
            type="button"
          >
            &#8249;
          </button>
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            title="Next page"
            type="button"
          >
            &#8250;
          </button>
          <button
            className={styles.pageButton}
            onClick={() => onPageChange(totalPages)}
            disabled={page >= totalPages}
            title="Last page"
            type="button"
          >
            &#187;
          </button>
        </div>
      </div>

      <div className={styles.progressBarTrack}>
        {loading && <div className={styles.progressBarFill} />}
      </div>

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.id} className={styles.th} style={getCellStyle(column.id)}>
                  {renderHeaderCell ? renderHeaderCell(column.title) : column.title}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr
                key={index}
                className={styles.tr}
                title={TooltipMessage || undefined}
                onDoubleClick={handleDoubleClick ? () => handleDoubleClick(row) : undefined}
              >
                {columns.map((column) => (
                  <td key={column.id} className={styles.td}>
                    {renderCell(row, column.id)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PaginatedTable;
